import type {Camera, IModifyTemplateGraph} from "@cm/template-nodes"
import type {
    InitMattressInstanceTemplateOptions,
    InitStackableMattressStandardModelMasterTemplateOptions,
} from "#mattress/assets-definitions/standard-models/common/model-factory"
import type {MattressSeedContext, StandardModelGroupKey, StandardModelKey} from "#mattress/assets-definitions/asset-ids"
import {
    requireAccessoryTemplateLegacyId,
    requireLightingTemplateLegacyId,
    requireMaterialLegacyId,
    requireReferencedAccessoryTemplateLegacyId,
    requireStandardModelMasterTemplateLegacyId as masterLegacyId,
} from "#mattress/assets-definitions/asset-ids"
import {
    initSimplePocketMasterTemplate,
    initSimplePocketInstanceTemplate,
    initPocketWithExtraLaneMasterTemplate,
    initPocketWithExtraLaneInstanceTemplate,
} from "#mattress/assets-definitions/standard-models/pocket"
import {
    initPocketTopperMasterTemplate,
    initPocketTopperInstanceTemplate,
    initWaterfallTopperMasterTemplate,
    initWaterfallTopperInstanceTemplate,
    initEdgeTopperMasterTemplate,
    initEdgeTopperInstanceTemplate,
} from "#mattress/assets-definitions/standard-models/topper"
import {
    initSimpleWaterfallMasterTemplate,
    initSimpleWaterfallInstanceTemplate,
    initWaterfallWithMirrorMasterTemplate,
    initWaterfallWithMirrorInstanceTemplate,
    initWaterfallWithExtraLaneMasterTemplate,
    initWaterfallWithExtraLaneInstanceTemplate,
    initWaterfallWithMirrorAndExtraLaneMasterTemplate,
    initWaterfallWithMirrorAndExtraLaneInstanceTemplate,
    initWaterfallBS006AMasterTemplate,
    initWaterfallBS006AInstanceTemplate,
    initWaterfallBS007AMasterTemplate,
    initWaterfallBS007AInstanceTemplate,
    initWaterfallBS012AMasterTemplate,
    initWaterfallBS012AInstanceTemplate,
} from "#mattress/assets-definitions/standard-models/waterfall"
import {
    initReversedWaterfallMasterTemplate,
    initReversedWaterfallInstanceTemplate,
    initReversedWaterfallWithMirrorMasterTemplate,
    initReversedWaterfallWithMirrorInstanceTemplate,
    initReversedWaterfallWithMirrorOnTopAndBottomMasterTemplate,
    initReversedWaterfallWithMirrorOnTopAndBottomInstanceTemplate,
} from "#mattress/assets-definitions/standard-models/reversed-waterfall"
import {
    initEdgeMasterTemplate,
    initEdgeInstanceTemplate,
    initEdgeWithExtraLaneMasterTemplate,
    initEdgeWithExtraLaneInstanceTemplate,
    initEdgeTopperLookMasterTemplate,
    initEdgeTopperLookInstanceTemplate,
    initPillowTopWithZipperMasterTemplate,
    initPillowTopWithZipperInstanceTemplate,
} from "#mattress/assets-definitions/standard-models/edge"
import {
    initAllOverWrapMasterTemplate,
    initAllOverWrapInstanceTemplate,
    initAllOverWrapWithMirrorMasterTemplate,
    initAllOverWrapWithMirrorInstanceTemplate,
} from "#mattress/assets-definitions/standard-models/all-over-wrap"

const masterTemplateOptions = (ctx: MattressSeedContext, openingOnBottom = false): InitStackableMattressStandardModelMasterTemplateOptions => ({
    openingOnBottom,
    topPartDefaultMaterialLegacyId: requireMaterialLegacyId(ctx, "defaultFabric"),
    bottomPartDefaultMaterialLegacyId: requireMaterialLegacyId(ctx, "defaultFabric"),
    middlePartDefaultMaterialLegacyId: requireMaterialLegacyId(ctx, "matteSubstrate"),
})

const instanceTemplateOptions = (ctx: MattressSeedContext, defaultCamera?: Camera): InitMattressInstanceTemplateOptions => ({
    defaultCamera,
    lightingTemplateLegacyId: requireLightingTemplateLegacyId(ctx),
    accessoriesTemplatesLegacyIds: {
        "piping 2mm": requireAccessoryTemplateLegacyId(ctx, "piping", "2mm"),
        "piping 5.5mm": requireAccessoryTemplateLegacyId(ctx, "piping", "5.5mm"),
        "zipper Reverse Coil, M3": requireAccessoryTemplateLegacyId(ctx, "zipper", "Reverse Coil, M3"),
        "zipper Reverse Coil, M5": requireAccessoryTemplateLegacyId(ctx, "zipper", "Reverse Coil, M5"),
        "zipper Spiral Coil, M3": requireAccessoryTemplateLegacyId(ctx, "zipper", "Spiral Coil, M3"),
        "zipper Spiral Coil, M5": requireAccessoryTemplateLegacyId(ctx, "zipper", "Spiral Coil, M5"),
        "zipper Teeth, M3": requireAccessoryTemplateLegacyId(ctx, "zipper", "Teeth, M3"),
        "zipper Teeth, M5": requireAccessoryTemplateLegacyId(ctx, "zipper", "Teeth, M5"),
        "tape 8mm": requireAccessoryTemplateLegacyId(ctx, "tape", "8mm"),
        "puller Round Big Hole Puller (r619) with Slider, M3": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Round Big Hole Puller (r619) with Slider, M3",
        ),
        "puller Round Big Hole Puller (r619) with Slider, M5": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Round Big Hole Puller (r619) with Slider, M5",
        ),
        "puller Square Big Hole Puller (r619) with Slider, M3": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Square Big Hole Puller (r619) with Slider, M3",
        ),
        "puller Square Big Hole Puller (r619) with Slider, M5": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Square Big Hole Puller (r619) with Slider, M5",
        ),
        "puller Square Puller Pointed (n51) with Slider, M3": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Square Puller Pointed (n51) with Slider, M3",
        ),
        "puller Square Puller Pointed (n51) with Slider, M5": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Square Puller Pointed (n51) with Slider, M5",
        ),
        "puller Standard Puller (YKK, n56) with Slider, M3": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Standard Puller (YKK, n56) with Slider, M3",
        ),
        "puller Standard Puller (YKK, n56) with Slider, M5": requireReferencedAccessoryTemplateLegacyId(
            ctx,
            "puller Standard Puller (YKK, n56) with Slider, M5",
        ),
        "puller Square Puller with Slider, M3": requireReferencedAccessoryTemplateLegacyId(ctx, "puller Square Puller with Slider, M3"),
        "vertical handle Default Vertical Handle": requireReferencedAccessoryTemplateLegacyId(ctx, "vertical handle Default Vertical Handle"),
        "horizontal handle Default Horizontal Handle": requireReferencedAccessoryTemplateLegacyId(ctx, "horizontal handle Default Horizontal Handle"),
        "horizontal handle Horizontal Handle, Cross Stitched": requireAccessoryTemplateLegacyId(ctx, "handle", "Horizontal Handle, Cross-Stitched"),
        "vertical handle Vertical Handle, 3.5cm": requireAccessoryTemplateLegacyId(ctx, "handle", "Vertical Handle, 3.5cm"),
        "vertical handle Vertical Handle, 5.5cm": requireAccessoryTemplateLegacyId(ctx, "handle", "Vertical Handle, 5.5cm"),
        "vertical handle Vertical Handle, 8cm": requireAccessoryTemplateLegacyId(ctx, "handle", "Vertical Handle, 8cm"),
        "vertical handle Vertical Handle with 2mm Piping, 8cm": requireAccessoryTemplateLegacyId(ctx, "handle", "Vertical Handle, 8cm with 2mm piping"),
        "verticla handle Vertical Handle with Tape, 6cm": requireAccessoryTemplateLegacyId(ctx, "handle", "Vertical Handle, 6cm with tape"),
        "verticla handle Vertical Handle with Tape, 8cm": requireAccessoryTemplateLegacyId(ctx, "handle", "Vertical Handle, 8m with tape"),
    },
})

export type StandardModelSeederFunctions = {
    master: (modify: IModifyTemplateGraph, ctx: MattressSeedContext) => Promise<void> | void
    instance: (modify: IModifyTemplateGraph, ctx: MattressSeedContext, defaultCamera?: Camera) => Promise<void> | void
}

export const standardModelTemplateSeeders = {
    pocket: {
        "simple pocket": {
            master: (modify, ctx) => initSimplePocketMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initSimplePocketInstanceTemplate(modify, masterLegacyId(ctx, "pocket", "simple pocket"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "pocket with extra lane": {
            master: (modify, ctx) => initPocketWithExtraLaneMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initPocketWithExtraLaneInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "pocket", "pocket with extra lane"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
    },
    topper: {
        "pocket topper": {
            master: (modify, ctx) => initPocketTopperMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initPocketTopperInstanceTemplate(modify, masterLegacyId(ctx, "topper", "pocket topper"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "waterfall topper": {
            master: (modify, ctx) => initWaterfallTopperMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initWaterfallTopperInstanceTemplate(modify, masterLegacyId(ctx, "topper", "waterfall topper"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "edge topper": {
            master: (modify, ctx) => initEdgeTopperMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initEdgeTopperInstanceTemplate(modify, masterLegacyId(ctx, "topper", "edge topper"), instanceTemplateOptions(ctx, defaultCamera)),
        },
    },
    waterfall: {
        "simple waterfall": {
            master: (modify, ctx) => initSimpleWaterfallMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initSimpleWaterfallInstanceTemplate(modify, masterLegacyId(ctx, "waterfall", "simple waterfall"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "waterfall with mirror": {
            master: (modify, ctx) => initWaterfallWithMirrorMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initWaterfallWithMirrorInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "waterfall", "waterfall with mirror"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
        "waterfall with extra lane": {
            master: (modify, ctx) => initWaterfallWithExtraLaneMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initWaterfallWithExtraLaneInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "waterfall", "waterfall with extra lane"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
        "waterfall with mirror and extra lane": {
            master: (modify, ctx) => initWaterfallWithMirrorAndExtraLaneMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initWaterfallWithMirrorAndExtraLaneInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "waterfall", "waterfall with mirror and extra lane"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
        "waterfall BS006A": {
            master: (modify, ctx) => initWaterfallBS006AMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initWaterfallBS006AInstanceTemplate(modify, masterLegacyId(ctx, "waterfall", "waterfall BS006A"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "waterfall BS007A": {
            master: (modify, ctx) => initWaterfallBS007AMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initWaterfallBS007AInstanceTemplate(modify, masterLegacyId(ctx, "waterfall", "waterfall BS007A"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "waterfall BS012A": {
            master: (modify, ctx) => initWaterfallBS012AMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initWaterfallBS012AInstanceTemplate(modify, masterLegacyId(ctx, "waterfall", "waterfall BS012A"), instanceTemplateOptions(ctx, defaultCamera)),
        },
    },
    "reversed waterfall": {
        "reversed waterfall": {
            master: (modify, ctx) => initReversedWaterfallMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initReversedWaterfallInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "reversed waterfall", "reversed waterfall"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
        "reversed waterfall with mirror": {
            master: (modify, ctx) => initReversedWaterfallWithMirrorMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initReversedWaterfallWithMirrorInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "reversed waterfall", "reversed waterfall with mirror"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
        "reversed waterfall with mirror on top and bottom": {
            master: (modify, ctx) => initReversedWaterfallWithMirrorOnTopAndBottomMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initReversedWaterfallWithMirrorOnTopAndBottomInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "reversed waterfall", "reversed waterfall with mirror on top and bottom"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
    },
    edge: {
        edge: {
            master: (modify, ctx) => initEdgeMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initEdgeInstanceTemplate(modify, masterLegacyId(ctx, "edge", "edge"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "edge with extra lane": {
            master: (modify, ctx) => initEdgeWithExtraLaneMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initEdgeWithExtraLaneInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "edge", "edge with extra lane"),
                    instanceTemplateOptions(ctx, defaultCamera),
                    requireMaterialLegacyId(ctx, "matteSubstrate"),
                ),
        },
        "edge topper look": {
            master: (modify, ctx) => initEdgeTopperLookMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initEdgeTopperLookInstanceTemplate(modify, masterLegacyId(ctx, "edge", "edge topper look"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "pillow top with zipper": {
            master: (modify, ctx) => initPillowTopWithZipperMasterTemplate(modify, masterTemplateOptions(ctx)),
            instance: (modify, ctx, defaultCamera) =>
                initPillowTopWithZipperInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "edge", "pillow top with zipper"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
    },
    "all over wrap": {
        "all over wrap": {
            master: (modify, ctx) => initAllOverWrapMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initAllOverWrapInstanceTemplate(modify, masterLegacyId(ctx, "all over wrap", "all over wrap"), instanceTemplateOptions(ctx, defaultCamera)),
        },
        "all over wrap with mirror": {
            master: (modify, ctx) => initAllOverWrapWithMirrorMasterTemplate(modify, masterTemplateOptions(ctx, true)),
            instance: (modify, ctx, defaultCamera) =>
                initAllOverWrapWithMirrorInstanceTemplate(
                    modify,
                    masterLegacyId(ctx, "all over wrap", "all over wrap with mirror"),
                    instanceTemplateOptions(ctx, defaultCamera),
                ),
        },
    },
} as const satisfies {[G in StandardModelGroupKey]: {[K in StandardModelKey<G>]: StandardModelSeederFunctions}}
